import { getToken } from "../storage/authStorage";
import { API_BASE_URL } from "../../../config/api";

/**
 * The tenant's own people: who they are, what role they hold, who reviews them.
 *
 * EVERY NUMBER ARRIVES AS A STRING. These endpoints return raw DB columns, so
 * `id`, `role_id`, `manager_id` and `is_active` come back as `"19"`, `"5"`,
 * `"18"`, `"1"`. In JS `"0"` is truthy and `"1" === 1` is false, which has
 * already produced three separate display bugs in this codebase (`tcs.is_expired`,
 * `currencies.price_precision`, and a bank flag). So the conversion happens here,
 * once, at the boundary — everything above this file gets real numbers and real
 * booleans and never has to remember.
 */

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

/** A person in the tenant, normalised. */
export interface TeamMember {
  id: number;
  email: string;
  userName: string | null;
  /** Role name as stored on the user row. */
  role: string | null;
  /**
   * May not resolve. `client@…` carries role_id 5 and there is no role 5 —
   * client_view is 10. Callers must handle a role_id with no matching role.
   */
  roleId: number | null;
  managerId: number | null;
  managerName: string | null;
  managerEmail: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
}

export interface TeamRole {
  id: number;
  name: string;
  /** From `roles.is_manager_required`. Drives whether a reviewer is mandatory. */
  managerRequired: boolean;
  description: string | null;
}

/** Someone who can be picked as a reviewer. */
export interface TeamPerson {
  id: number;
  email: string;
  userName: string | null;
  role: string | null;
}

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toText = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return s === "" ? null : s;
};

const request = async <T>(
  path: string,
  method: "GET" | "POST",
  body?: unknown,
): Promise<T> => {
  const token = getToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Everyone in the tenant, active and not.
 *
 * `limit` is capped at 100 by the endpoint, which rejects anything higher with a
 * 400 rather than clamping. A tenant is a single accounting firm's team, so 100
 * is not a real ceiling — but if it is ever reached this silently shows a subset,
 * which is why the total is returned alongside.
 */
export const fetchTeamMembers = async (): Promise<{
  members: TeamMember[];
  total: number;
}> => {
  const raw = await request<{ data: any[]; total: number }>(
    "/api/users?limit=100",
    "GET",
  );

  const members = (raw.data ?? []).map(
    (row): TeamMember => ({
      id: Number(row.id),
      email: String(row.email ?? ""),
      userName: toText(row.userName),
      role: toText(row.role),
      roleId: toNumber(row.role_id),
      managerId: toNumber(row.manager_id),
      managerName: toText(row.manager_name),
      managerEmail: toText(row.manager_email),
      isActive: Number(row.is_active) === 1,
      lastLoginAt: toText(row.date_last_logged_in),
    }),
  );

  return { members, total: Number(raw.total ?? members.length) };
};

/** The roles this tenant defines. Admin or CFO only. */
export const fetchTeamRoles = async (): Promise<TeamRole[]> => {
  const raw = await request<{ data: any[] }>("/api/roles/list", "POST", {});

  return (raw.data ?? []).map(
    (row): TeamRole => ({
      id: Number(row.id),
      name: String(row.role_name ?? ""),
      managerRequired: Number(row.is_manager_required) === 1,
      description: toText(row.description),
    }),
  );
};

/**
 * Candidates for the reviewer slot: every active user.
 *
 * The endpoint deliberately includes the caller — an admin may be their own
 * team's reviewer. It does not exclude the user being edited, so the caller has
 * to; the backend rejects self-assignment with a 400 either way.
 */
export const fetchAssignableManagers = async (): Promise<TeamPerson[]> => {
  const raw = await request<{ data: any[] }>("/api/managers/assignable", "GET");

  return (raw.data ?? []).map(
    (row): TeamPerson => ({
      id: Number(row.id),
      email: String(row.email ?? ""),
      userName: toText(row.userName),
      role: toText(row.role),
    }),
  );
};

/**
 * Change a person's role, their reviewer, or both.
 *
 * These two fields are the only ones a tenant admin may write —
 * `UserController::update` refuses email, name, picture and active flag with a
 * comment saying they belong to master admin. Sending `manager_id: null`
 * clears the reviewer; omitting the key leaves it alone.
 *
 * A role change (not a reviewer change) also fires `RoleSyncService` at the
 * master admin for this environment.
 */
export const updateTeamMember = async (payload: {
  id: number;
  role_id?: number;
  manager_id?: number | null;
}): Promise<{ success: boolean; message: string }> =>
  request<{ success: boolean; message: string }>("/api/users/update", "POST", payload);

/**
 * Take someone off the team.
 *
 * Sets `is_active = 0` and tells master admin to unassign them from this
 * company. The row stays, so this is reversible — via `reactivateTeamMember`
 * below (`update` still cannot write `is_active`).
 */
export const deactivateTeamMember = async (
  id: number,
): Promise<{ success: boolean; message: string }> =>
  request<{ success: boolean; message: string }>("/api/users/delete", "POST", { id });

/**
 * The reverse: `is_active` back to 1, and master is told to re-register the
 * assignment (sync-role). Admin only, same as deactivate. Creating a NEW
 * login is still master-only — this only flips an existing row's switch.
 */
export const reactivateTeamMember = async (
  id: number,
): Promise<{ success: boolean; message: string }> =>
  request<{ success: boolean; message: string }>("/api/users/reactivate", "POST", { id });

/**
 * The module names a role is allowed to touch.
 *
 * Used for the read-only "Can do" line. The tenant's real permission model is
 * module × action (18 modules × 38 actions) and every stored row is a grant, so
 * the set of modules present IS the set the role can reach.
 */
export const fetchRoleModules = async (roleId: number): Promise<string[]> => {
  const raw = await request<{
    data: { permissions?: { module_name?: string; actions?: Record<string, { is_granted?: boolean }> }[] };
  }>(`/api/roles/get?role_id=${roleId}`, "GET");

  return (raw.data?.permissions ?? [])
    .filter((p) => Object.values(p.actions ?? {}).some((a) => a?.is_granted))
    .map((p) => String(p.module_name ?? ""))
    .filter((name) => name !== "");
};

// ── Maker–checker team board (six module tabs) ──────────────────────────────

export type SeatKind = "maker" | "checker";
export type TabPolicy = "post" | "review" | "blocked";

export interface BoardPerson {
  userId: number;
  name: string;
  email: string;
}

export interface BoardFunction {
  key: string;
  label: string;
  module: string;
  moduleLabel: string;
  policy: TabPolicy;
  default: TabPolicy;
  changed: boolean;
}

export interface BoardTab {
  key: string;
  label: string;
  modules: string[];
  makers: BoardPerson[];
  checkers: BoardPerson[];
  functions: BoardFunction[];
  counts: Record<TabPolicy, number>;
  gate: {
    status: "ok" | "empty" | "block" | "warn";
    message: string | null;
    makers: number;
    checkers: number;
    reviews: number;
  };
}

export interface BoardMember {
  userId: number;
  name: string;
  email: string;
  role: string;
  isZn: boolean;
  seats: Record<string, SeatKind>;
}

export interface TeamBoardState {
  tabs: BoardTab[];
  members: BoardMember[];
  unseated: BoardMember[];
  validation: {
    ok: boolean;
    blocks: { scope: string; tab: string | null; label: string | null; message: string }[];
    warnings: { scope: string; tab: string | null; label: string | null; message: string }[];
  };
}

/** What save/validate send: the whole board, atomically. */
export interface TeamBoardConfig {
  seats: Record<string, Record<number, SeatKind>>;
  policies: Record<string, TabPolicy>;
}

const toBoardState = (raw: any): TeamBoardState => {
  const person = (p: any): BoardPerson => ({
    userId: Number(p.user_id),
    name: String(p.name ?? ""),
    email: String(p.email ?? ""),
  });
  const member = (m: any): BoardMember => ({
    userId: Number(m.user_id),
    name: String(m.name ?? ""),
    email: String(m.email ?? ""),
    role: String(m.role ?? ""),
    isZn: Boolean(m.is_zn),
    seats: (m.seats ?? {}) as Record<string, SeatKind>,
  });

  return {
    tabs: (raw.tabs ?? []).map((t: any): BoardTab => ({
      key: String(t.key),
      label: String(t.label),
      modules: t.modules ?? [],
      makers: (t.makers ?? []).map(person),
      checkers: (t.checkers ?? []).map(person),
      functions: (t.functions ?? []).map((f: any): BoardFunction => ({
        key: String(f.key),
        label: String(f.label),
        module: String(f.module),
        moduleLabel: String(f.module_label ?? f.module),
        policy: f.policy as TabPolicy,
        default: f.default as TabPolicy,
        changed: Boolean(f.changed),
      })),
      counts: t.counts ?? { post: 0, review: 0, blocked: 0 },
      gate: t.gate ?? { status: "ok", message: null, makers: 0, checkers: 0, reviews: 0 },
    })),
    members: (raw.members ?? []).map(member),
    unseated: (raw.unseated ?? []).map(member),
    validation: raw.validation ?? { ok: true, blocks: [], warnings: [] },
  };
};

export const fetchTeamBoardState = async (): Promise<TeamBoardState> => {
  const raw = await request<{ data: any }>("/api/team-assignment/state", "GET");
  return toBoardState(raw.data);
};

/** Save the whole board in one transaction; the fresh state comes back. */
export const saveTeamBoard = async (config: TeamBoardConfig): Promise<TeamBoardState> => {
  const raw = await request<{ data: any }>("/api/team-assignment/save", "POST", config);
  return toBoardState(raw.data);
};

/**
 * One escalation contact, as pushed from master admin at go-live.
 *
 * The tenant never writes these: master's PushEscalationsStep empties and
 * re-fills `escalation_contacts` on every run, so this list is exactly what
 * the onboarding wizard collected. Display-only — the mailer still resolves
 * recipients by role and ignores this table.
 */
export interface EscalationContact {
  id: number;
  /** 1-based escalation order — first in the list is contacted first. */
  position: number;
  name: string;
  email: string;
  phone: string | null;
  /** Where master got the contact: 'firm' | 'books' | 'manual'. */
  source: string;
}

/** The escalation contacts in order. Empty until master pushes them. */
export const fetchEscalationContacts = async (): Promise<EscalationContact[]> => {
  const raw = await request<{ data?: { contacts?: any[] } }>(
    "/api/escalation-contacts",
    "GET",
  );

  return (raw.data?.contacts ?? []).map(
    (row): EscalationContact => ({
      id: Number(row.id),
      position: Number(row.position),
      name: String(row.name ?? ""),
      email: String(row.email ?? ""),
      phone: toText(row.phone),
      source: String(row.source ?? ""),
    }),
  );
};
